/* oxlint-disable no-param-reassign */
import { useQueryClient } from "@tanstack/react-query";
import type { Cell, ColumnDef, Row } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import clsx from "clsx";
import { produce } from "immer";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteIcon } from "@/assets/icons/general/Delete";
import { DownloadIcon } from "@/assets/icons/general/Download";
import { Edit2Icon } from "@/assets/icons/general/Edit2";
import { MarkAsReadIcon } from "@/assets/icons/general/MarkAsRead";
import { RefreshIcon } from "@/assets/icons/general/Refresh";
import { StarWideIcon } from "@/assets/icons/general/StarWide";
import { VerticalDotsIcon } from "@/assets/icons/general/VerticalDots";
import { StarIcon } from "@/assets/icons/ui/Star";
import FileUploadStatusIcon from "@/components/shared/common/FileUploadStatusIcon";
import { Button } from "@/components/ui/buttons/Button/Button";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { TextButton } from "@/components/ui/buttons/TextButton/TextButton";
import { TextArea } from "@/components/ui/inputs/TextInput/TextArea";
import { Dropdown } from "@/components/ui/overlays/Dropdown/Dropdown";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { useToast } from "@/components/ui/status/Toast/useToast";
import TableCheckboxCell from "@/components/ui/table/TableCheckboxCell";
import TableContentCell from "@/components/ui/table/TableContentCell";
import TableHeaderCell from "@/components/ui/table/TableHeaderCell";
import { TableTypographyCell } from "@/components/ui/table/TableTypographyCell";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { getUploadsProfileRoute } from "@/config/route.config";
import { BizProfileModels } from "@/data/bizProfile/bizProfile.models";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";
import { FileUtils } from "@/util/file.utils";
import { logger } from "@/util/logger";
import { NumberUtils } from "@/util/number.utils";

const useUploadsPageColumns = ({
  setIsDeleteProfileModalOpen,
  openEditUploadsModal,
}: {
  setIsDeleteProfileModalOpen: (row?: BizProfileModels.BizProfileListDto) => void;
  openEditUploadsModal: (row?: BizProfileModels.BizProfileListDto) => void;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { successToast, errorToast } = useToast();
  const queryClient = useQueryClient();
  const [downloadId, setDownloadId] = useState<string | null>(null);

  const invalidateBizProfileList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [...BizProfileQueries.keys.all],
    });
  }, [queryClient]);

  const { refetch: downloadBizProfile } = BizProfileQueries.useDownloadResume(
    { id: downloadId || "" },
    { enabled: false },
  );

  const { mutate: favoriteBizProfile } = BizProfileQueries.useFavorite({
    onSuccess: () => {
      invalidateBizProfileList();
      successToast({
        text: t("uploads.userUploads.favorite-success"),
      });
    },
  });

  const { mutate: unFavoriteBizProfile } = BizProfileQueries.useUnfavorite({
    onSuccess: () => {
      invalidateBizProfileList();
      successToast({
        text: t("uploads.userUploads.unfavorite-success"),
      });
    },
  });

  const { mutate: updateBizProfile } = BizProfileQueries.useUpdate({
    onSuccess: () => {
      invalidateBizProfileList();
      successToast({
        text: t("uploads.userUploads.update-success"),
      });
    },
  });

  const { mutate: retryProcessingPipeline } = BizProfileQueries.useRetryProcessingPipeline({
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({
        queryKey: [...BizProfileQueries.keys.all],
      });

      const previousData = queryClient.getQueryData([...BizProfileQueries.keys.all]);

      queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, (old: any) => {
        if (!old?.items) return old;

        return produce(old, (draft: any) => {
          draft.items.forEach((item: any) => {
            if (item.id === id) {
              item.status = BizProfileModels.BizProfileListStatusEnum.queued;
              item.statusReason = null;
            }
          });
        });
      });

      return { previousData };
    },
    onError: (err, _variables, context: unknown) => {
      const typedContext = context as { previousData: unknown } | undefined;
      if (typedContext?.previousData) {
        queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, typedContext.previousData);
      }
      errorToast({
        text: t("uploads.userUploads.retry-processing-pipeline-error"),
      });
      logger.error("Error retrying processing pipeline", { err });
    },
  });

  const { mutate: markAsRead } = BizProfileQueries.useMarkAsRead({
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({
        queryKey: [...BizProfileQueries.keys.all],
      });

      const previousData = queryClient.getQueryData([...BizProfileQueries.keys.all]);

      queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, (old: any) => {
        if (!old?.items) return old;

        return produce(old, (draft: any) => {
          draft.items.forEach((item: any) => {
            if (item.id === id) {
              // oxlint-disable-next-line no-param-reassign
              item.isRead = true;
            }
          });
        });
      });

      return { previousData };
    },
    onError: (err, _variables, context: unknown) => {
      const typedContext = context as { previousData: unknown } | undefined;
      if (typedContext?.previousData) {
        queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, typedContext.previousData);
      }
      logger.error("Failed to mark profile as read", { error: err });
    },
  });

  const handleRowDelete = (row?: BizProfileModels.BizProfileListDto) => {
    if (!row) {
      return;
    }
    setIsDeleteProfileModalOpen(row);
  };

  const handleDownload = async (id?: string, name?: string) => {
    if (!id) {
      return;
    }
    setDownloadId(id);

    setTimeout(async () => {
      try {
        const result = await downloadBizProfile();

        FileUtils.handleFileDownload(result?.data?.url, name);
      } catch {
        errorToast({
          text: t("uploads.userUploads.download-error"),
        });
      }
    }, 0);
  };

  const handleRowFavoriteToggle = (id: string, isFavorited: boolean) => {
    if (!id) {
      return;
    }
    if (isFavorited) {
      unFavoriteBizProfile({ id });
    } else {
      favoriteBizProfile({ id });
    }
  };

  const handleRowCommentChange = (comment: string, id: string) => {
    if (!id) {
      return;
    }
    updateBizProfile({ id, data: { comment } });
  };

  const handleRowRatingChange = (rating?: number, id?: string, oldRating?: number | null) => {
    if (!id) {
      return;
    }
    updateBizProfile({ id, data: { rating: oldRating === rating ? -1 : rating } });
  };

  const handleRowRetryProcessingPipeline = async (id?: string, name?: string) => {
    if (!id) {
      return;
    }
    retryProcessingPipeline(
      { id },
      {
        onSuccess: () => {
          successToast({
            text: t("uploads.userUploads.retry-processing-pipeline", { name }),
          });
        },
      },
    );
  };

  const handleNavigateToRowProfile = async (id?: string, isRead?: boolean) => {
    if (!id) {
      return;
    }

    if (!isRead) {
      markAsRead({ id });
    }

    router.push(getUploadsProfileRoute(id));
  };

  const isRowProcessed = (row: BizProfileModels.BizProfileListDto) => {
    return row.status === BizProfileModels.BizProfileListStatusEnum.processed;
  };

  const isRowSelectableForCheckbox = (row: BizProfileModels.BizProfileListDto) => {
    return (
      row.status === BizProfileModels.BizProfileListStatusEnum.processed ||
      row.status === BizProfileModels.BizProfileListStatusEnum.processing_failed
    );
  };

  const handleMarkAsRead = (id?: string) => {
    if (!id) {
      return;
    }
    markAsRead(
      { id },
      {
        onSuccess: () => {
          successToast({
            text: t("uploads.userUploads.mark-as-read-success"),
          });
        },
      },
    );
  };

  const handleEdit = (row?: BizProfileModels.BizProfileListDto) => {
    if (!row) {
      return;
    }
    openEditUploadsModal(row);
  };

  const columns = useMemo<ColumnDef<BizProfileModels.BizProfileListDto>[]>(
    () => [
      {
        accessorKey: "checkbox",
        header: ({ table }) => (
          <TableCheckboxCell
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={() => {
              const allRows = table.getRowModel().rows;
              const selectableRows = allRows.filter((row: any) => isRowSelectableForCheckbox(row.original));

              const currentSelection = table.getState().rowSelection;
              const selectableRowIds = selectableRows.map((row) => row.id);

              // Check how many selectable rows are currently selected
              const selectedSelectableCount = selectableRowIds.filter((id) => currentSelection[id]).length;
              const shouldSelect = selectedSelectableCount < selectableRowIds.length;

              // Create new selection state
              const newSelection = { ...currentSelection };

              selectableRowIds.forEach((rowId) => {
                if (shouldSelect) {
                  newSelection[rowId] = true;
                } else {
                  delete newSelection[rowId];
                }
              });

              table.setRowSelection(newSelection);
            }}
          />
        ),

        meta: {
          filterKey: "checkbox",
          filterVariant: "text",
        },
        size: 40,
        enableSorting: false,
        cell: ({ row, cell }) => UploadsTableCells(row, cell, "checkbox", isRowSelectableForCheckbox(row.original)),
      },
      {
        accessorKey: "isRead",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label=""
          />
        ),
        size: 12,
        minSize: 12,
        maxSize: 12,
        enableSorting: false,
        cell: ({ row, cell }) => UnreadTableCell(row.original, cell, isRowProcessed(row.original)),
      },
      {
        accessorKey: "isFavorited",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label=""
          />
        ),
        meta: {
          filterKey: "isFavorited",
          filterVariant: "text",
        },
        size: 50,
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        cell: ({ row, cell }) =>
          FavoriteTableCell(row.original, cell, handleRowFavoriteToggle, isRowProcessed(row.original)),
      },
      {
        accessorKey: "rank",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.rank")}
            align="center"
          />
        ),
        meta: {
          filterKey: "rank",
          filterVariant: "text",
        },
        size: 50,
        maxSize: 50,
        minSize: 50,
        enableSorting: true,
        sortDescFirst: false,

        cell: ({ cell, row }) =>
          UploadsTableCells(
            row,
            cell,
            "rank",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "name",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.profile")}
            align="start"
          />
        ),
        meta: {
          filterKey: "name",
          filterVariant: "text",
        },
        size: 150,
        maxSize: 150,
        minSize: 150,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "profile",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "jobTitle",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("filters.jobTitle")}
            align="start"
          />
        ),
        meta: {
          filterKey: "jobTitle",
          filterVariant: "text",
        },
        size: 150,
        maxSize: 150,
        minSize: 80,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "jobTitle",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "ldNumber",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.ld-number")}
            align="end"
          />
        ),
        meta: {
          filterKey: "ldNumber",
          filterVariant: "text",
        },
        size: 120,
        maxSize: 120,
        minSize: 120,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "ldNumber",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "impliedSalary",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.impliedSalary")}
            align="end"
          />
        ),
        size: 120,
        maxSize: 120,
        minSize: 120,
        enableSorting: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "impliedSalary",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "degrees",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.degrees")}
            align="start"
          />
        ),
        meta: {
          filterKey: "degrees",
          filterVariant: "text",
        },
        size: 200,
        maxSize: 200,
        minSize: 100,
        enableSorting: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "degrees",
            isRowProcessed(row.original),
            handleRowRetryProcessingPipeline,
            undefined,
            handleNavigateToRowProfile,
          ),
      },

      {
        accessorKey: "experienceYears",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.experience")}
            align="center"
          />
        ),
        meta: {
          filterKey: "experience",
          filterVariant: "text",
        },
        enableSorting: true,
        sortDescFirst: false,
        size: 40,
        maxSize: 40,
        minSize: 40,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "experience",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "comment",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.comment")}
            align="center"
          />
        ),
        meta: {
          filterKey: "comment",
          filterVariant: "text",
        },
        size: 180,
        maxSize: 180,
        enableSorting: false,
        cell: ({ row, cell }) =>
          CommentTableCell(row.original, cell, isRowProcessed(row.original), handleRowCommentChange),
      },
      {
        accessorKey: "rating",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.rating")}
            align="center"
          />
        ),
        meta: {
          filterKey: "rating",
          filterVariant: "number",
        },
        size: 60,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "rating",
            isRowProcessed(row.original),
            handleRowRetryProcessingPipeline,
            handleRowRatingChange,
          ),
      },
      {
        accessorKey: "tags",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label={t("uploads.filters.tags")}
            align="center"
          />
        ),
        meta: {
          filterKey: "tags",
          filterVariant: "text",
        },
        size: 130,
        maxSize: 130,
        minSize: 80,
        enableSorting: false,
        cell: ({ row, cell }) =>
          UploadsTableCells(
            row,
            cell,
            "tags",
            isRowProcessed(row.original),
            undefined,
            undefined,
            handleNavigateToRowProfile,
          ),
      },
      {
        accessorKey: "actions",
        header: ({ header }) => (
          <TableHeaderCell
            header={header}
            label=""
          />
        ),
        meta: {
          filterKey: "actions",
          filterVariant: "text",
        },
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableSorting: false,
        cell: ({ row }) =>
          UploadsTableActions(
            row.original,
            handleRowDelete,
            handleDownload,
            handleRowRetryProcessingPipeline,
            handleMarkAsRead,
            handleEdit,
            isRowProcessed(row.original),
          ),
      },
    ],
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  return { columns };
};

export const UploadsTableCells = (
  row: Row<BizProfileModels.BizProfileListDto>,
  cell: Cell<BizProfileModels.BizProfileListDto, unknown>,
  key: string,
  isProcessed: boolean,
  handleRetryProcessingPipeline?: (id?: string, name?: string) => void,
  onRatingChange?: (rating?: number, rowId?: string, oldRating?: number | null) => void,
  onCellClick?: (id?: string, isRead?: boolean) => void,
) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (key === "degrees" && !isProcessed) {
    const statusIcon = () => {
      switch (row.original.status) {
        case "processing":
          return <Loader />;
        case "processing_failed":
          return <FileUploadStatusIcon status="error" />;
        case "queued":
          return <FileUploadStatusIcon status="idle" />;
        default:
          return null;
      }
    };
    const statusLabel = () => {
      switch (row.original.status) {
        case "processing":
          return t("uploads.userUploads.processing");
        case "processing_failed":
          return t("uploads.userUploads.failed");
        case "queued":
          return t("uploads.userUploads.queued");
        default:
          return null;
      }
    };
    return (
      <TableContentCell className="flex flex-row items-center gap-2">
        <div>{statusIcon()}</div>

        {row.original.status !== "processing_failed" && <Typography size="body-4">{statusLabel()}</Typography>}

        {row.original.status === "processing_failed" && (
          <Tooltip text={row.original.statusReason || t("uploads.userUploads.retry")}>
            <Button
              variant="text"
              className="!pl-0 !text-info-error"
              width="auto"
              labelClassName="!text-desktop-body-4 !font-primary"
              onPress={() => handleRetryProcessingPipeline?.(row.original.id, row.original.name)}
            >
              <Typography
                size="body-4"
                className="text-info-error"
              >
                {statusLabel()}.
              </Typography>
              <Typography
                size="body-4"
                className="text-info-error underline underline-offset-1"
              >
                {t("uploads.userUploads.retry")}
              </Typography>
            </Button>
          </Tooltip>
        )}
      </TableContentCell>
    );
  }

  if (key !== "profile" && !isProcessed) {
    return null;
  }

  if (key === "checkbox") {
    return (
      <TableCheckboxCell
        checked={row.getIsSelected()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    );
  }

  if (key === "isFavorited") {
    return (
      <TableContentCell className="flex items-center justify-center">
        <StarIcon
          stroke="red"
          className={clsx("h-4 w-4", cell.getValue() ? "fill-elevation-surface-4" : "text-elevation-outline-2")}
        />
      </TableContentCell>
    );
  }
  if (key === "name") {
    return (
      <TableContentCell
        align="start"
        onClick={isProcessed ? () => onCellClick?.(row.original.id, row.original.isRead) : undefined}
      >
        <Typography
          size="body-3"
          className="line-clamp-2 whitespace-pre-line break-words font-body-prominent-2 capitalize"
        >
          {cell.getValue<string>().toLowerCase().replace(".pdf", "")}
        </Typography>
      </TableContentCell>
    );
  }

  if (key === "tags") {
    const visibleTags = row.original.tags?.slice(0, 3);
    const remainingTags = row.original.tags?.slice(3) || [];
    const remainingTagsCount = remainingTags.length;
    const maxTagLength = 20;

    return (
      <TableContentCell
        onClick={() => onCellClick?.(row.original.id, row.original.isRead)}
        align="start"
      >
        <div className="flex flex-row flex-wrap items-center justify-center gap-2">
          {visibleTags?.map((tag: any) => (
            <div
              key={tag}
              className="flex gap-1-5 rounded-xs bg-interactive-tertiary-idle px-1-5 py-1 shadow-1"
            >
              <Tooltip text={tag.length > maxTagLength ? tag : null}>
                <CustomTooltipTrigger>
                  <Typography
                    size="label-5"
                    variant="prominent-2"
                  >
                    {tag.length > maxTagLength ? `${tag.slice(0, maxTagLength)}...` : tag}
                  </Typography>
                </CustomTooltipTrigger>
              </Tooltip>
            </div>
          ))}
          {remainingTagsCount > 0 && (
            <Tooltip
              tooltipContent={
                <div className="flex max-w-[300px] flex-col gap-1 p-2">
                  <div className="flex flex-wrap gap-1">
                    {remainingTags.map((tag: any) => (
                      <div
                        key={tag}
                        className="rounded-button-rounding bg-interactive-tertiary-idle px-1-5 py-0-5"
                      >
                        <Typography size="body-3">{tag}</Typography>
                      </div>
                    ))}
                  </div>
                </div>
              }
              placement="bottom"
            >
              <CustomTooltipTrigger className="!bg-interactive-tertiary-idle !text-text-default-secondary relative flex gap-1-5 rounded-xs px-1-5 py-1 shadow-1">
                <Typography
                  size="label-5"
                  variant="prominent-2"
                >
                  + {remainingTagsCount}
                </Typography>
              </CustomTooltipTrigger>
            </Tooltip>
          )}
        </div>
      </TableContentCell>
    );
  }

  if (key === "experience") {
    return (
      <TableContentCell onClick={() => onCellClick?.(row.original.id, row.original.isRead)}>
        <div className="flex flex-col items-start gap-2">
          <Typography size="body-3">{row.original.experienceYears || 0}y</Typography>
        </div>
      </TableContentCell>
    );
  }

  if (key === "ldNumber") {
    return (
      <TableContentCell
        onClick={() => onCellClick?.(row.original.id, row.original.isRead)}
        className="h-full"
        align="end"
      >
        <Typography size="body-3">{NumberUtils.formatAsUSCurrency(row.original.jobSalary || 0)}</Typography>
      </TableContentCell>
    );
  }

  if (key === "impliedSalary") {
    return (
      <TableContentCell
        align="end"
        className="h-full"
      >
        <Typography size="body-3">{NumberUtils.formatAsUSCurrency(row.original.impliedSalary || 0)}</Typography>
      </TableContentCell>
    );
  }

  if (key === "rank") {
    return (
      <TableContentCell
        onClick={() => onCellClick?.(row.original.id, row.original.isRead)}
        align="center"
      >
        <Typography size="body-3">{row.original.rank ? `#${row.original.rank}` : ""}</Typography>
      </TableContentCell>
    );
  }

  if (key === "rating") {
    const getRatingColor = () => {
      switch (row.original.rating) {
        case 1:
          return "text-text-default-primary";
        case 2:
          return "text-text-default-primary";
        case 3:
          return "text-text-default-primary";
        case 4:
          return "text-text-default-primary";
        case 5:
          return "text-text-default-primary";
        default:
          return "text-text-default-primary";
      }
    };

    const ratingOptions = [
      { id: 1, name: "1" },
      { id: 2, name: "2" },
      { id: 3, name: "3" },
      { id: 4, name: "4" },
      { id: 5, name: "5" },
    ];
    return (
      <TableContentCell
        className="flex items-center justify-center"
        align="center"
      >
        <Dropdown
          useTriggerWidth={false}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          customTrigger={
            <TextButton
              className={clsx("!p-2", getRatingColor())}
              iconPosition="right"
              onPress={() => setIsOpen(true)}
              size="body-4"
              variant="prominent-2"
            >
              {row.original.rating ? row.original.rating.toString() : "/"}
            </TextButton>
          }
          popoverProps={{
            className: "w-[70px]",
          }}
        >
          <div className="flex flex-col gap-1-5">
            {ratingOptions.map((option) => (
              <div
                className={clsx(
                  "flex cursor-pointer items-center justify-center gap-3 rounded-button-rounding p-2 hover:bg-elevation-surface-2",
                  option.id === row.original.rating && "bg-interactive-text-pressed",
                )}
                onClick={() => {
                  setIsOpen(false);
                  onRatingChange?.(option.id, row.original.id, row.original.rating || null);
                }}
                key={option.id}
              >
                <Typography
                  variant="prominent-1"
                  size="body-3"
                >
                  {option.name}
                </Typography>
              </div>
            ))}
          </div>
        </Dropdown>
      </TableContentCell>
    );
  }
  if (key === "degrees") {
    const visibleDegrees = row.original.degrees?.slice(0, 3);
    const remainingDegrees = row.original.degrees?.slice(3) || [];
    const remainingDegreesCount = remainingDegrees.length;

    return (
      <TableContentCell
        align="start"
        onClick={() => onCellClick?.(row.original.id, row.original.isRead)}
      >
        <div className="flex flex-row flex-wrap items-start gap-2">
          <Tooltip
            tooltipContent={
              remainingDegreesCount > 0 ? (
                <div className="flex max-w-[300px] flex-col gap-1 p-2">
                  <div className="flex flex-wrap gap-1">
                    {remainingDegrees.map((degree: any, index: number) => (
                      <Typography
                        size="body-3"
                        key={index}
                      >
                        {degree}
                      </Typography>
                    ))}
                  </div>
                </div>
              ) : null
            }
          >
            <CustomTooltipTrigger>
              {visibleDegrees?.map((degree: any, index: number) => (
                <Typography
                  size="body-3"
                  key={index}
                >
                  {degree}
                </Typography>
              ))}
            </CustomTooltipTrigger>
          </Tooltip>
        </div>
      </TableContentCell>
    );
  }

  if (key === "jobTitle") {
    return (
      <TableContentCell
        align="start"
        onClick={isProcessed ? () => onCellClick?.(row.original.id, row.original.isRead) : undefined}
        className="whitespace-normal"
      >
        <div className="flex flex-col items-start justify-center gap-2">
          <Typography
            size="body-3"
            className="line-clamp-2 whitespace-pre-line break-words font-body-prominent-2 capitalize"
          >
            {cell.getValue<string>()}
          </Typography>
          <Typography
            size="body-3"
            className="text-text-default-quarternary"
          >
            {row.original.location}
          </Typography>
        </div>
      </TableContentCell>
    );
  }

  if (key === "profile") {
    return (
      <TableContentCell
        align="start"
        onClick={isProcessed ? () => onCellClick?.(row.original.id, row.original.isRead) : undefined}
        className="whitespace-normal"
      >
        <div className="flex flex-col items-start justify-center gap-2">
          <Typography
            size="body-3"
            className="line-clamp-2 whitespace-pre-line break-words font-body-prominent-2 capitalize"
          >
            {row.original.name}
          </Typography>
          <Typography
            size="body-3"
            className="text-text-default-quarternary"
          >
            {row.original.residentLocation}
          </Typography>
        </div>
      </TableContentCell>
    );
  }

  return <TableTypographyCell value={cell.getValue<string>()} />;
};

export const CommentTableCell = (
  rowData: BizProfileModels.BizProfileListDto,
  cell: Cell<BizProfileModels.BizProfileListDto, unknown>,
  isProcessed: boolean,
  onCommentChangeConfirm: (comment: string, id: string) => void,
) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState(rowData.comment || "");
  const [isTextAreaOpen, setIsTextAreaOpen] = useState(false);

  useEffect(() => {
    setComment(rowData.comment || "");
  }, [rowData.comment]);

  if (!isProcessed) {
    return null;
  }

  return (
    <TableContentCell align="start">
      {!isTextAreaOpen && (
        <Tooltip
          text={comment || undefined}
          className="!bg-elevation-background"
          pointerClassName="!text-elevation-background"
        >
          <CustomTooltipTrigger>
            <Typography
              size="body-3"
              className={cx(
                "line-clamp-3 max-w-44 cursor-pointer whitespace-pre-line break-words text-left",
                !comment && "text-text-inverted-secondary",
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsTextAreaOpen(true);
              }}
            >
              {comment || t("uploads.userUploads.add-comment")}
            </Typography>
          </CustomTooltipTrigger>
        </Tooltip>
      )}
      {isTextAreaOpen && (
        <TextArea
          label=""
          variant="chat"
          placeholder={t("uploads.userUploads.add-comment")}
          value={comment}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onChange={(e) => setComment(e.target.value)}
          autoFocus
          onBlur={() => {
            setIsTextAreaOpen(false);
            onCommentChangeConfirm(comment, rowData.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setIsTextAreaOpen(false);
              onCommentChangeConfirm(comment, rowData.id);
            }
          }}
        />
      )}
    </TableContentCell>
  );
};

const UploadsTableActions = (
  row: Partial<BizProfileModels.BizProfileListDto>,
  handleDelete: (row?: BizProfileModels.BizProfileListDto) => void,
  handleDownload: (id?: string, name?: string) => void,
  handleRetry: (id?: string, name?: string) => void,
  handleMarkAsRead: (id?: string, name?: string) => void,
  handleEdit: (row?: BizProfileModels.BizProfileListDto) => void,
  isProcessed: boolean,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  if (!isProcessed && (row.status === "processing" || row.status === "queued")) {
    return null;
  }

  return (
    <TableContentCell className="flex items-center justify-center opacity-0 group-hover:opacity-100">
      <Dropdown
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        customTrigger={
          <IconButton
            icon={VerticalDotsIcon}
            variant="outlined"
            label=""
            className="!p-1 border-none bg-transparent"
          />
        }
        popoverProps={{
          placement: "bottom right",
          className: "w-36",
        }}
        useTriggerWidth={false}
      >
        <div className="flex w-full flex-col gap-3-5">
          {isProcessed && (
            <>
              <Button
                icon={MarkAsReadIcon}
                variant="text"
                className="!justify-start !p-1 items-center text-text-default-primary hover:bg-elevation-outline-1"
                labelClassName="!text-desktop-body-3 "
                iconClassName="w-6 h-6"
                onPress={() => {
                  setIsOpen(false);
                  handleMarkAsRead(row.id);
                }}
              >
                {t("uploads.userUploads.mark-as-read")}
              </Button>
              <Button
                icon={Edit2Icon}
                variant="text"
                className="!justify-start !p-1 items-center text-text-default-primary hover:bg-elevation-outline-1"
                labelClassName="!text-desktop-body-3"
                iconClassName="w-6 h-6"
                onPress={() => {
                  setIsOpen(false);
                  handleEdit(row as BizProfileModels.BizProfileListDto);
                }}
              >
                {t("uploads.userUploads.edit")}
              </Button>

              <Button
                icon={RefreshIcon}
                variant="text"
                className="!justify-start !p-1 items-center text-text-default-primary hover:bg-elevation-outline-1"
                labelClassName="!text-desktop-body-3"
                iconClassName="w-6 h-6"
                onPress={() => {
                  setIsOpen(false);
                  handleRetry(row.id, row.name);
                }}
              >
                {t("uploads.userUploads.re-process")}
              </Button>
              <Button
                icon={DownloadIcon}
                variant="text"
                className="!items-center !justify-start !gap-1 !p-1 !pl-1 text-text-default-primary hover:bg-elevation-outline-1"
                labelClassName="!text-desktop-body-3 "
                iconClassName="w-6 h-6"
                onPress={() => {
                  setIsOpen(false);
                  handleDownload(row.id, row.name);
                }}
              >
                {t("uploads.userUploads.download")}
              </Button>
            </>
          )}

          <Button
            icon={DeleteIcon}
            variant="text"
            className="!justify-start !p-1 items-center hover:bg-elevation-outline-1"
            labelClassName="!text-desktop-body-3 text-info-error"
            iconClassName="w-6 h-6 text-info-error"
            onPress={() => {
              setIsOpen(false);
              handleDelete(row as BizProfileModels.BizProfileListDto);
            }}
          >
            {t("uploads.userUploads.delete")}
          </Button>
        </div>
      </Dropdown>
    </TableContentCell>
  );
};

export const FavoriteTableCell = (
  row: Partial<BizProfileModels.BizProfileListDto>,
  cell: Cell<any, unknown>,
  onFavoriteToggle: (id: string, isFavorited: boolean) => void,
  isProcessed: boolean,
) => {
  const handleFavorite = () => {
    if (!row?.id) {
      return;
    }
    onFavoriteToggle(row.id, !!row.isFavorited);

    // oxlint-disable-next-line no-param-reassign
    row.isFavorited = !row.isFavorited;
  };

  if (!isProcessed) {
    return null;
  }

  return (
    <TableContentCell className="flex items-center justify-center">
      <IconButton
        icon={StarWideIcon}
        className={clsx("!border-none !bg-transparent !p-1 h-full w-full")}
        iconClassName={clsx(
          "h-4 w-4",
          row.isFavorited ? "fill-elevation-surface-4 text-elevation-surface-4" : "text-elevation-outline-2",
        )}
        onPress={handleFavorite}
        label=""
      />
    </TableContentCell>
  );
};

export const UnreadTableCell = (
  row: Partial<BizProfileModels.BizProfileListDto>,
  cell: Cell<any, unknown>,
  isProcessed: boolean,
) => {
  if (!isProcessed) {
    return null;
  }

  return (
    <TableContentCell className="!p-0 flex items-center justify-center">
      {row.isRead ? "" : <div className="h-2-5 w-2-5 rounded-full bg-elevation-surface-4 p-0-5" />}
    </TableContentCell>
  );
};

export default useUploadsPageColumns;
